/**
 * «По номерам» на экране «Лиды»: лиды периода по уникальному номеру — сколько
 * раз звонили и чем кончилось. Спека `docs/specs/2026-09-24-leads-phones-design.md`;
 * числа — те же, что у `prokat-runner leads-phones`.
 */
import { useQuery } from "@tanstack/react-query";
import { CircleCheck } from "lucide-react";
import { Link, useSearchParams } from "react-router";

import {
  daysAgo,
  type LeadPhones,
  loadLeadPhones,
  type Outcome,
  OUTCOME_LABEL,
  type PhoneRow,
} from "@/lib/leadPhones";
import { LeadLink } from "@/components/LeadLink";
import { shortTime, workTime } from "@/lib/leads";

const ORDER: Outcome[] = ["untouched", "deal_no_contact", "tried", "chat", "in_talk", "out_talk"];
const TONE: Record<Outcome, string> = {
  untouched: "text-err",
  deal_no_contact: "text-warn",
  tried: "text-warn",
  chat: "text-fg",
  in_talk: "text-fg",
  out_talk: "text-ok",
};

/** Период и фильтр — в адресе (`pfrom`, `pto`, `outcome`): ссылку можно переслать. */
function usePhonesParams() {
  const [params, setParams] = useSearchParams();
  const p = {
    from: params.get("pfrom") || daysAgo(1),
    to: params.get("pto") || params.get("pfrom") || daysAgo(1),
    outcome: (params.get("outcome") as Outcome | null) || null,
  };
  const set = (next: Partial<typeof p>) => {
    const v = { ...p, ...next };
    const out = new URLSearchParams(params);
    out.set("pfrom", v.from);
    out.set("pto", v.to);
    if (v.outcome) out.set("outcome", v.outcome);
    else out.delete("outcome");
    setParams(out, { replace: true });
  };
  return [p, set] as const;
}

function Stat({ label, value, hint, active, onClick }: {
  label: string; value: number | string; hint?: string; active?: boolean; onClick?: () => void;
}) {
  const body = (
    <>
      <div className="text-[12px] text-fg-muted">{label}</div>
      <div className="text-[22px] font-semibold leading-none tabular-nums">{value}</div>
      {hint && <div className="text-[11.5px] text-fg-faint">{hint}</div>}
    </>
  );
  const cls = "flex min-w-0 flex-col gap-1 rounded-[var(--radius-card)] border bg-bg-subtle p-3 text-left";
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`${cls} ${active ? "border-accent" : "border-border hover:bg-bg-hover"}`}
      data-annot={`итог «${label}»`}
    >
      {body}
    </button>
  ) : (
    <div className={`${cls} border-border`} data-annot={`воронка «${label}»`}>
      {body}
    </div>
  );
}

function Attempts({ row }: { row: PhoneRow }) {
  if (!row.attempts.length) return <span className="text-fg-faint">—</span>;
  return (
    <span className="flex flex-wrap gap-x-2 gap-y-0.5">
      {row.attempts.map((a) => (
        <span
          key={a.at}
          className={`inline-flex items-center gap-0.5 whitespace-nowrap tabular-nums ${a.talk ? "text-ok" : "text-fg-muted"}`}
          title={a.talk ? `разговор ${a.sec} с` : "без разговора"}
        >
          {shortTime(a.at)}
          {a.talk && (
            <>
              <CircleCheck size={11} aria-hidden /> <span className="sr-only">разговор</span>
            </>
          )}
        </span>
      ))}
    </span>
  );
}

function Body({ r, outcome, setOutcome }: { r: LeadPhones; outcome: Outcome | null; setOutcome: (o: Outcome | null) => void }) {
  const f = r.funnel;
  const rows = outcome ? r.phones.filter((p) => p.outcome === outcome) : r.phones;
  const maxAttempts = Math.max(1, ...Object.values(r.attempts));
  return (
    <>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="Новых лидов" value={f.leads} />
        <Stat label="С номером РФ" value={f.valid_phone} hint={`без номера ${f.leads - f.valid_phone}`} />
        <Stat label="В работе" value={f.in_work} hint={`спам, дубли, сервисные: ${f.valid_phone - f.in_work}`} />
        <Stat label="Уникальных номеров" value={f.phones} />
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {ORDER.map((o) => (
          <Stat
            key={o}
            label={OUTCOME_LABEL[o]}
            value={r.outcomes[o]}
            hint={`${f.phones ? Math.round((r.outcomes[o] / f.phones) * 100) : 0}% номеров`}
            active={outcome === o}
            onClick={() => setOutcome(outcome === o ? null : o)}
          />
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-6 text-[12.5px]">
        <div data-annot="исходящих на номер">
          <div className="mb-1 text-[12px] text-fg-muted">Исходящих на номер</div>
          <div className="flex items-end gap-2">
            {Object.entries(r.attempts).map(([k, v]) => (
              <div key={k} className="flex w-9 flex-col items-center gap-1" title={`${k} исходящих: ${v} номеров`}>
                <span className="tabular-nums">{v}</span>
                <div className="w-full rounded-t-[3px] bg-accent" style={{ height: `${Math.max(2, (v / maxAttempts) * 48)}px` }} />
                <span className="text-fg-faint">{k}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="text-fg-muted" data-annot="первый исходящий">
          Первый исходящий: медиана <b className="text-fg">{workTime(r.first_out.median)}</b>, p90{" "}
          {workTime(r.first_out.p90)} · рабочее время {r.work_hours}
        </div>
      </div>
      <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
        <table className="w-full text-[12.5px]">
          <thead className="text-left text-[11.5px] text-fg-faint">
            <tr>
              {["Номер", "Лиды", "Источник", "Статус", "Итог", "Первый исходящий", "Исх. / разговор", "Вход. разговор", "Попытки"].map((h) => (
                <th key={h} className="whitespace-nowrap px-3 py-2 font-medium">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.phone} className="border-t border-border align-top hover:bg-bg-hover" data-annot={`номер ${p.phone}`}>
                <td className="whitespace-nowrap px-3 py-1.5 font-mono tabular-nums">{p.phone}</td>
                <td className="px-3 py-1.5 font-mono tabular-nums" title={p.leads.map((l) => `${l.lead_id} ${shortTime(l.created)}`).join("\n")}>
                  {p.leads.map((l, i) => (
                    <span key={l.lead_id}>
                      {i > 0 && ", "}
                      <LeadLink id={l.lead_id} />
                      {l.deal_id && (
                        <Link to={`/deals/${l.deal_id}`} className="ml-1 text-accent hover:underline" title="карточка сделки">
                          → {l.deal_id}
                        </Link>
                      )}
                    </span>
                  ))}
                  <div className="font-sans text-[11.5px] text-fg-faint">{shortTime(p.created)}</div>
                </td>
                <td className="px-3 py-1.5">{p.leads[0]?.source}</td>
                <td className="px-3 py-1.5">{p.status}</td>
                <td className={`whitespace-nowrap px-3 py-1.5 font-medium ${TONE[p.outcome]}`}>{OUTCOME_LABEL[p.outcome]}</td>
                <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">
                  {p.first_out ? (
                    <>
                      {shortTime(p.first_out)}
                      <div className="text-[11.5px] text-fg-faint">через {workTime(p.first_out_min)}</div>
                    </>
                  ) : (
                    <span className="text-fg-faint">—</span>
                  )}
                </td>
                <td className="px-3 py-1.5 tabular-nums">
                  {p.out} / {p.out_talk}
                </td>
                <td className="px-3 py-1.5 tabular-nums">{p.in_talk || <span className="text-fg-faint">—</span>}</td>
                <td className="px-3 py-1.5">
                  <Attempts row={p} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <div className="p-4 text-[12.5px] text-fg-muted">Номеров нет.</div>}
      </div>
    </>
  );
}

export function LeadPhonesSection() {
  const [p, set] = usePhonesParams();
  const q = useQuery({ queryKey: ["lead-phones", p.from, p.to], queryFn: () => loadLeadPhones(p.from, p.to) });
  const input = "h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] text-fg";
  const btn = "h-8 rounded-[var(--radius-ui)] border border-border px-2.5 text-fg-muted hover:bg-bg-hover";
  const today = daysAgo(0);
  return (
    <section className="flex flex-col gap-3" data-annot="по номерам">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[12px] text-fg-faint">лиды периода по уникальному номеру; звонки — с создания лида до сейчас, чаты — с ответом оператора; → номер сделки открывает её карточку</span>
        <div className="ml-auto flex flex-wrap items-center gap-1.5 text-[12.5px]" data-annot="период по номерам">
          <button type="button" className={btn} onClick={() => set({ from: daysAgo(1), to: daysAgo(1) })}>
            вчера
          </button>
          <button type="button" className={btn} onClick={() => set({ from: daysAgo(7), to: daysAgo(1) })}>
            7 дней
          </button>
          <button type="button" className={btn} onClick={() => set({ from: daysAgo(30), to: daysAgo(1) })}>
            30 дней
          </button>
          <input type="date" value={p.from} max={today} onChange={(e) => e.target.value && set({ from: e.target.value })} className={input} />
          <span className="text-fg-faint">—</span>
          <input type="date" value={p.to} max={today} onChange={(e) => e.target.value && set({ to: e.target.value })} className={input} />
        </div>
      </div>
      {q.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Считаю по CRM…</div>
      ) : q.isError ? (
        <div className="text-[12.5px] text-err">Не удалось получить разрез по номерам: {String(q.error.message)}</div>
      ) : (
        <Body r={q.data} outcome={p.outcome} setOutcome={(o) => set({ outcome: o })} />
      )}
    </section>
  );
}
